// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Car, Star, MapPin, Clock, Mail, Shield, Globe } from "lucide-react";
import Home from "./pages/Home";
import Drivers from "./pages/Drivers";
import Contact from "./pages/Contact";
import "./App.css"; // global CSS import (not module)

// Navigation component with active state tracking
function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/drivers", label: "Drivers" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`navbar ${isScrolled ? "navbarScrolled" : ""}`}>
      <div className="navContainer">
        <div className="navContent">
          {/* Logo */}
          <Link to="/" className="logo">
            <div className="logoIcon">
              <Car className="logoCarIcon" />
            </div>
            <div className="logoText">
              <span className="logoBrand">Mariam Tours</span>
              <span className="logoTagline">Premium Private Tours</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="desktopNav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`navButton ${isActive(item.path) ? "navButtonActive" : ""}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop Contact Buttons */}
          <div className="desktopContact">
            <a href="tel:+201104625154" className="contactButton">
              <Phone className="contactIcon" />
              <span>Call Now</span>
            </a>
            <a
              href="https://wa.me/+201274948282"
              target="_blank"
              rel="noopener noreferrer"
              className="contactButton primaryContact"
            >
              <MessageCircle className="contactIcon" />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobileMenuButton"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="menuIcon" />
            ) : (
              <Menu className="menuIcon" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobileMenu">
            <div className="mobileMenuContent">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobileNavButton ${isActive(item.path) ? "mobileNavButtonActive" : ""}`}
                >
                  {item.label}
                </Link>
              ))}

              {/* Mobile Contact Buttons */}
              <div className="mobileContactButtons">
                <a href="tel:+201001234567" className="mobileContactButton">
                  <Phone className="mobileContactIcon" />
                  <span>Call Now</span>
                </a>
                <a
                  href="https://wa.me/201001234567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobileContactButton mobilePrimaryContact"
                >
                  <MessageCircle className="mobileContactIcon" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Footer component
function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footerContent">
        <div className="footerSection">
          <div className="footerBrand">
            <div className="footerLogo">
              <Car className="footerLogoIcon" />
              <span>Mariam Tours</span>
            </div>
            <p className="footerDescription">
              Premium private car tours with professional drivers. Safe,
              reliable, and unforgettable travel experiences across Egypt.
            </p>
          </div>
        </div>

        <div className="footerSection">
          <h4 className="footerTitle">Quick Links</h4>
          <ul className="footerLinks">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/drivers">Our Drivers</Link>
            </li>
            <li>
              <Link to="/contact">Contact Us</Link>
            </li>
          </ul>
        </div>

        <div className="footerSection">
          <h4 className="footerTitle">Contact Info</h4>
          <div className="footerContact">
            <div className="footerContactItem">
              <Phone className="footerContactIcon" />
              <span>+20 100 123 4567</span>
            </div>
            <div className="footerContactItem">
              <Mail className="footerContactIcon" />
              <span>info@mariamtours.com</span>
            </div>
            <div className="footerContactItem">
              <MapPin className="footerContactIcon" />
              <span>Cairo, Egypt</span>
            </div>
            <div className="footerContactItem">
              <Clock className="footerContactIcon" />
              <span>24/7 Available</span>
            </div>
          </div>
        </div>

        <div className="footerSection">
          <h4 className="footerTitle">Why Choose Us</h4>
          <div className="footerFeatures">
            <div className="footerFeature">
              <Shield className="footerFeatureIcon" />
              <span>Licensed & Insured</span>
            </div>
            <div className="footerFeature">
              <Star className="footerFeatureIcon" />
              <span>5-Star Rated</span>
            </div>
            <div className="footerFeature">
              <Globe className="footerFeatureIcon" />
              <span>Multilingual Service</span>
            </div>
          </div>
        </div>
      </div>

      <div className="footerBottom">
        <div className="footerBottomContent">
          <p className="footerCopyright">
            © {currentYear} Mariam Tours. All rights reserved.
          </p>
          <div className="footerLegal">
            <a href="/privacy" className="footerLegalLink">
              Privacy Policy
            </a>
            <a href="/terms" className="footerLegalLink">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main App component
function App() {
  return (
    <Router>
      <div className="app">
        <Navigation />

        <main className="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
